import React from "react";
import { Pressable, Text, View } from "react-native";
import { useRouter } from "expo-router";
import { format } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

type Conversation = {
    id: number;
    name: string;
    type: string;
    jobId: number | null;
    lastMessage: string;
    lastMessageSender: string;
    lastMessageTimestamp: Date;
    hasUnread: boolean;
    participants: Array<{
        id: number;
        isAdmin: boolean;
        staffId: number | null;
        crewId: number | null;
        staffName: string | null;
        crewName: string | null;
        avatar: string | null;
    }>;
};

interface ConversationListItemProps {
    conversation: Conversation;
    isActive?: boolean;
}

export function ConversationListItem({ conversation, isActive = false }: ConversationListItemProps) {
    const router = useRouter();
    const formattedTime = format(new Date(conversation.lastMessageTimestamp), "h:mm a");

    // Format the date - if today, show time, otherwise show date
    const formattedDate = format(new Date(conversation.lastMessageTimestamp), "MMM d");
    const isToday = new Date(conversation.lastMessageTimestamp).toDateString() === new Date().toDateString();
    const dateTimeDisplay = isToday ? formattedTime : formattedDate;

    // Truncate last message if it's too long
    const truncatedMessage = conversation.lastMessage.length > 40
        ? conversation.lastMessage.substring(0, 40) + "..."
        : conversation.lastMessage;

    // Get avatar fallback (initials)
    const getInitials = (name: string) => {
        if (!name) return "?";
        const words = name.trim().split(/\s+/);
        if (words.length === 1) return words[0].charAt(0).toUpperCase();
        return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    };

    // Determine avatar based on conversation type
    const getAvatarInfo = () => {
        const fallbackText = getInitials(conversation.name);
        let avatarImageUrl = null;

        // For different conversation types, use different fallback colors
        let fallbackColor = "bg-primary";

        if (conversation.type === "JOB") {
            fallbackColor = "bg-blue-600";
        } else if (conversation.type === "GROUP") {
            fallbackColor = "bg-purple-600";
        } else {
            fallbackColor = "bg-emerald-600";
        }

        // For direct messages, try to find the staff user's avatar
        if (conversation.participants.length > 0) {
            const otherParticipant = conversation.participants.find(p => p.staffId !== null);
            if (otherParticipant?.avatar) {
                // Use the avatar directly from the participant data
                avatarImageUrl = otherParticipant.avatar;
            }
        }

        return { fallbackText, avatarImageUrl, fallbackColor };
    };

    const { fallbackText, avatarImageUrl, fallbackColor } = getAvatarInfo();

    return (
        <Pressable
            className={`flex-row p-4 border-b border-gray-200 bg-white ${isActive ? "bg-gray-100" : ""} ${conversation.hasUnread ? "bg-emerald-50" : ""}`}
            onPress={() => {
                router.navigate(`/(app)/messages/${conversation.id}`);
            }}
        >
            <Avatar className="h-12 w-12 mr-4" alt={conversation.name}>
                {avatarImageUrl && (
                    <AvatarImage
                        source={{ uri: avatarImageUrl }}
                        className="h-full w-full"
                    />
                )}
                <AvatarFallback className={`${fallbackColor}`}>
                    <Text className="text-white text-lg font-bold">{fallbackText}</Text>
                </AvatarFallback>
            </Avatar>

            <View className="flex-1 justify-center">
                <View className="flex-row justify-between items-center">
                    <View className="flex-row items-center">
                        <Text className={`text-base ${conversation.hasUnread ? "font-bold text-gray-900" : "font-medium text-gray-800"}`}>
                            {conversation.name}
                        </Text>
                        {conversation.hasUnread && (
                            <View className="ml-2 h-2 w-2 rounded-full bg-emerald-500" />
                        )}
                    </View>
                    <Text className="text-xs text-gray-500">{dateTimeDisplay}</Text>
                </View>

                <View className="flex-row mt-1">
                    <Text className={`text-sm ${conversation.hasUnread ? "font-medium text-gray-800" : "text-gray-500"} flex-1`}>
                        <Text className="font-medium">{conversation.lastMessageSender}: </Text>
                        {truncatedMessage}
                    </Text>
                </View>
            </View>
        </Pressable>
    );
} 